import { Injectable, inject } from '@angular/core';
import { AnyAbility } from '@casl/ability';
import { AbilityService } from '@casl/angular';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  GeneralCompanySettings,
  RoleAction,
  RolePermissionSubject,
} from '@wilson/interfaces';
import {
  catchError,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { GeneralCompanySettingsGatewayService } from '../general-company-settings-gateway.service';
import {
  CreateGeneralGeneralCompanySettings,
  FetchGeneralCompanySettingsIfNeeded,
  ForceFetchGeneralCompanySettings,
  PatchGeneralCompanySettingsLeaveCarryoverState,
  PatchGeneralCompanySettingsManagerApprovalState,
  PatchGeneralCompanySettingsMasterDeciderApprovalState,
  UpdateGeneralGeneralCompanySettings,
} from './general-settings.actions';

export type GeneralSettingsStateModel = GeneralCompanySettings;

const defaults: GeneralSettingsStateModel = {
  address: {
    city: '',
    country: '',
    street: '',
    postCode: '',
    state: '',
  },
  companyLogo: '',
  email: '',
  generalManager: '',
  id: '',
  localCourt: '',
  organizationalUnitId: '',
  phone: '',
  taxIdentificationNumber: '',
  tradeRegisterNumber: '',
  vatId: '',
  websiteUrl: '',
  isInitialized: false,
  isLoading: false,
  masterDeciderMustApproveAbsence: false,
  managerMustApproveAbsence: false,
  usePreviousYearCarryOverVacation: false,
};

export const GENERAL_COMPANY_SETTINGS_STATE_KEY = 'generalCompanySettings';

@State<GeneralSettingsStateModel>({
  name: GENERAL_COMPANY_SETTINGS_STATE_KEY,
  defaults,
})
@Injectable()
export class GeneralCompanySettingsState {
  private readonly generalCompanySettingsGatewayService = inject(
    GeneralCompanySettingsGatewayService,
  );
  private readonly abilityService =
    inject<AbilityService<AnyAbility>>(AbilityService);

  @Selector()
  static isManagerApprovalRequired(state: GeneralSettingsStateModel): boolean {
    return !!state.managerMustApproveAbsence;
  }

  @Selector()
  static isMasterDeciderApprovalRequired(
    state: GeneralSettingsStateModel,
  ): boolean {
    return !!state.masterDeciderMustApproveAbsence;
  }

  @Selector()
  static isLeaveCarryoverEnabled(state: GeneralSettingsStateModel): boolean {
    return !!state.usePreviousYearCarryOverVacation;
  }

  @Selector()
  static generalCompanySettings(
    state: GeneralSettingsStateModel,
  ): GeneralCompanySettings {
    return state;
  }

  @Action(UpdateGeneralGeneralCompanySettings)
  async updateGeneralCompanyAddress(
    ctx: StateContext<GeneralSettingsStateModel>,
    action: UpdateGeneralGeneralCompanySettings,
  ): Promise<void> {
    const currentView = ctx.getState();

    ctx.setState(patch(action.generalCompanySettings));

    try {
      await this.generalCompanySettingsGatewayService.updateGeneralSettings(
        ctx.getState().id as string,
        action.generalCompanySettings,
      );

      if (action.generalCompanySettings.addressId === null) {
        action.generalCompanySettings.address = {
          city: '',
          country: '',
          street: '',
          postCode: '',
          state: '',
        };
      }

      ctx.patchState(action.generalCompanySettings);
    } catch (e) {
      ctx.patchState(currentView);
      return Promise.reject(e);
    }
  }

  @Action(PatchGeneralCompanySettingsManagerApprovalState)
  patchGeneralGeneralCompanySettingsManagerApprovalState(
    { patchState }: StateContext<GeneralSettingsStateModel>,
    action: PatchGeneralCompanySettingsManagerApprovalState,
  ): void {
    patchState({
      managerMustApproveAbsence: action.isManagerApprovalRequired,
    });
  }

  @Action(PatchGeneralCompanySettingsMasterDeciderApprovalState)
  patchGeneralGeneralCompanySettingsMasterDeciderApprovalState(
    { patchState }: StateContext<GeneralSettingsStateModel>,
    action: PatchGeneralCompanySettingsMasterDeciderApprovalState,
  ): void {
    patchState({
      masterDeciderMustApproveAbsence: action.isMasterDeciderApprovalRequired,
    });
  }

  @Action(PatchGeneralCompanySettingsLeaveCarryoverState)
  patchLeaveCarryoverState(
    { patchState }: StateContext<GeneralSettingsStateModel>,
    action: PatchGeneralCompanySettingsLeaveCarryoverState,
  ): void {
    patchState({
      usePreviousYearCarryOverVacation: action.isLeaveCarryoverEnabled,
    });
  }

  @Action(CreateGeneralGeneralCompanySettings)
  async createGeneralGeneralCompanySettings(
    ctx: StateContext<GeneralSettingsStateModel>,
    action: UpdateGeneralGeneralCompanySettings,
  ): Promise<void> {
    const currentView = ctx.getState();

    ctx.setState(patch(action.generalCompanySettings));

    try {
      const generalSettings =
        await this.generalCompanySettingsGatewayService.createGeneralSettings(
          action.generalCompanySettings,
        );
      ctx.patchState(generalSettings);
    } catch (e) {
      ctx.patchState(currentView);
      return Promise.reject(e);
    }
  }

  @Action(FetchGeneralCompanySettingsIfNeeded)
  fetchGeneralCompanySettingsIfNeeded(
    ctx: StateContext<GeneralSettingsStateModel>,
  ): Observable<GeneralCompanySettings | null> {
    const state = ctx.getState();
    if (!state.isLoading && !state.isInitialized) {
      ctx.patchState({ isLoading: true });
      return this.fetchGeneralSettings(ctx, true);
    } else {
      return of(null);
    }
  }

  @Action(ForceFetchGeneralCompanySettings)
  forceFetchGeneralCompanySettings(
    ctx: StateContext<GeneralSettingsStateModel>,
  ): Observable<GeneralCompanySettings | null> {
    const state = ctx.getState();
    if (!state.isLoading) {
      ctx.patchState({ isLoading: true });
      return this.fetchGeneralSettings(ctx, false);
    } else {
      return of(null);
    }
  }

  private fetchGeneralSettings(
    ctx: StateContext<GeneralSettingsStateModel>,
    updateInitializationState: boolean,
  ): Observable<GeneralCompanySettings | null> {
    return this.abilityService.ability$.pipe(
      map((ability) =>
        ability.can(RoleAction.Read, RolePermissionSubject.BankAccount),
      ),
      filter((permission) => !!permission),
      switchMap(() => {
        return this.generalCompanySettingsGatewayService.getAll().pipe(
          take(1),
          tap((generalSettings) => {
            const stateUpdate: Partial<GeneralSettingsStateModel> = {
              ...generalSettings,
            };

            if (updateInitializationState) {
              stateUpdate.isInitialized = true;
            }

            ctx.patchState(stateUpdate);
          }),
          catchError(() => {
            if (updateInitializationState) {
              ctx.patchState({
                isInitialized: false,
              });
            }
            return of(null);
          }),
          finalize(() => {
            ctx.patchState({
              isLoading: false,
            });
          }),
        );
      }),
    );
  }
}
